import { MouseEvent, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { ViewColumnsIcon } from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import Menu from "components/DesignComponents/Menu";
import TableContext from "context/table/tableContext";
import { MenuAnchorEl, RectangleLoader } from "shared";

import ColumnsToShow from "./ColumnsToShow";
import InitialContent from "./InitialContent";
import RowsPerPage from "./RowsPerPage";
import { TABLE_VIEW_CONTENT, TableViewProps } from "./types";

const TableView = ({ isInitialLoading }: TableViewProps) => {
    const { columns, hasNoPagination, hasNoPermissionToRead } =
        useContext(TableContext);

    const { t } = useTranslation();

    const [content, setContent] = useState<TABLE_VIEW_CONTENT>(
        TABLE_VIEW_CONTENT.Initial
    );

    const [anchorEl, setAnchorEl] = useState<MenuAnchorEl>(null);

    const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>) => {
        setContent(TABLE_VIEW_CONTENT.Initial);
        setAnchorEl(e.currentTarget);
    };

    const handleCloseMenu = () => setAnchorEl(null);

    const handleSetContent = (newContent: TABLE_VIEW_CONTENT) =>
        setContent(newContent);

    const renderContent = () => {
        switch (content) {
            case TABLE_VIEW_CONTENT.Columns:
                return <ColumnsToShow setContent={handleSetContent} />;
            case TABLE_VIEW_CONTENT.Rows:
                return <RowsPerPage setContent={handleSetContent} />;
            default:
                return <InitialContent setContent={handleSetContent} />;
        }
    };

    const isDisabled =
        hasNoPermissionToRead || (columns.length === 0 && hasNoPagination);

    return isInitialLoading ? (
        <RectangleLoader
            width={105}
            height={24}
            customStyle={{
                marginLeft: "24px",
                marginRight: "12px",

                "@media(max-width: 599px)": {
                    marginLeft: "0",
                    marginTop: "8px"
                }
            }}
        />
    ) : (
        <>
            <Button
                color={BUTTON_COLORS.White}
                size={BUTTON_SIZES.Small}
                variant={BUTTON_VARIANTS.IconLeft}
                icon={<ViewColumnsIcon />}
                disabled={isDisabled}
                onClick={handleOpenMenu}
                css={css({
                    marginLeft: "16px",

                    "@media(max-width: 599px)": {
                        marginTop: "8px",
                        marginLeft: "0"
                    }
                })}
            >
                {t("Button##table view")}
            </Button>

            <Menu
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorEl={anchorEl}
                slotProps={{
                    list: { disableListWrap: true }
                }}
                css={css({
                    "& .MuiMenu-paper": {
                        maxHeight: "384px",
                        minWidth: "276px",
                        maxWidth: "276px"
                    }
                })}
            >
                {renderContent()}
            </Menu>
        </>
    );
};

export default TableView;
